import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useImageGameController } from '../controllers/useImageGameController';
import { normalize } from '@utils/stringUtils.js';
import JoinPromptDialog from '@components/JoinPromptDialog.jsx';
import ImageGameSummary from './ImageGameSummary';
import FilteredImage from '../components/FilteredImage.jsx';
import { ddragonVersion } from '@api/championService';
import '../../GameQuestion/styles/QuestionGameView.css';
import '../styles/ImageGameView.css';

const isMobile = window.innerWidth <= 500;

export default function ImageGameView() {
    const { id: lobbyCode } = useParams();
    const {
        refs: { inputRef },
        state,
        actions,
        lifecycle
    } = useImageGameController();

    const {
        scoreboard, questions, currentIndex, isCreator, isSpectator, timeLeft,
        duration, progress, selectedAnswer, revealed, correctAnswer, gameOver,
        showFinalScreen, imageDifficulties, pixelationStrength, answerLocked,
        highlightedIndex, showJoinDialog, joinError, forcePassword, isLoading,
        suggestions, showSuggestions, socket
    } = state;

    useEffect(() => {
        lifecycle.reconnect();
    }, []);

    useEffect(() => {
        if (inputRef.current && !revealed && !isSpectator && timeLeft > 0 && !answerLocked) {
            inputRef.current.focus();
        }
    }, [currentIndex, revealed, isSpectator, timeLeft, answerLocked]);

    useEffect(() => {
        const nextIndex = currentIndex + 1;
        if (questions[nextIndex]) {
            const img = new Image();
            img.src = questions[nextIndex].imageUrl;
        }
    }, [currentIndex, questions]);

    const playerAnswerState = (player) => {
        if (!revealed) return "";
        if (normalize(player.lastAnswer) === normalize(correctAnswer)) return "correct";
        if (player.lastAnswer && normalize(player.lastAnswer) !== normalize(correctAnswer)) return "wrong";
        return "";
    };

    if (isLoading) return <div className="loading-container"><p>Lade Spiel...</p></div>;
    if (showJoinDialog) {
        return <JoinPromptDialog
            lobbyCode={lobbyCode}
            onSubmit={({ name, password }) => socket.emit('spectatorJoin', {
                lobbyCode,
                user: { name, persistentId: state.persistentId, socketId: socket.id },
                password
            }, resp => {
                if (resp.success) {
                    actions.setShowJoinDialog(false);
                    lifecycle.applyFullGameState(resp.gameState, resp.scoreboard, resp.reveal);
                } else {
                    actions.setJoinError(resp.message);
                }
            })}
            onClose={() => actions.handleLeaveLobby()}
            error={joinError}
            forcePassword={forcePassword}
        />;
    }

    if (showFinalScreen) {
        const summaryProps = {
            scoreboard: scoreboard.filter(p => !p.isSpectator),
            handleLeaveLobby: actions.handleLeaveLobby,
            handleReturnToLobby: actions.handleReturnToLobby
        };
        return <ImageGameSummary {...summaryProps} />;
    }

    return (
        <div className="image-gameview-wrapper">
            <div className="countdown-display">{timeLeft}s</div>
            <div className="question-progress">
                Frage {currentIndex + 1} / {questions.length}
            </div>

            <div className="game-layout">
                <div className="players-column left">
                    {scoreboard.filter((p) => !p.isSpectator).filter((_, i) => i % 2 === 0).map((player, i) => (
                        <div
                            key={`${player.persistentId || 'unknown'}-${i}`}
                            className={`player-card ${playerAnswerState(player)} ${!revealed && player.lastAnswer ? 'locked' : ''}`}
                        >
                            <div className="player-name">{player.name}</div>
                            <div className="player-score">{player.score}</div>
                            {revealed && player.lastAnswer && (
                                <div className="player-answer">Antwort: {player.lastAnswer}</div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="game-area">
                    <div className="question-container">
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>

                        <FilteredImage
                            src={questions[currentIndex]?.imageUrl}
                            alt="Champion Splash Art"
                            className="question-image"
                            difficulties={imageDifficulties}
                            pixelationStrength={pixelationStrength}
                        />

                        {isSpectator && <div className="spectator-badge">Zuschauer-Modus</div>}

                        <div className="answer-input">
                            <input
                                ref={inputRef}
                                type="text"
                                value={selectedAnswer}
                                onChange={actions.handleInputChange}
                                onKeyDown={actions.handleKeyDown}
                                onBlur={() => setTimeout(() => actions.setShowSuggestions(false), 200)}
                                placeholder={answerLocked ? "Antwort gesperrt" : "Gib den Champion-Namen ein"}
                                disabled={revealed || timeLeft === 0 || isSpectator || answerLocked}
                                autoFocus
                            />

                            {showSuggestions && (
                                <div className="suggestions-list">
                                    {suggestions.map((sug, idx) => (
                                        <div
                                            key={`suggestion-${idx}`}
                                            className={`suggestion-item ${idx === highlightedIndex ? 'highlighted' : ''}`}
                                            onClick={() => actions.handleSuggestionClick(sug)}
                                        >
                                            <img
                                                src={`https://ddragon.leagueoflegends.com/cdn/${ddragonVersion}/img/champion/${sug.key}.png`}
                                                alt={sug.name}
                                                className="suggestion-icon"
                                            />
                                            {sug.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {revealed && (
                            <div className={`reveal ${normalize(selectedAnswer) === normalize(correctAnswer) ? 'correct' : 'wrong'}`}>
                                Richtige Antwort: {correctAnswer}
                            </div>
                        )}
                    </div>
                </div>

                <div className="players-column right">
                    {scoreboard.filter((p) => !p.isSpectator).filter((_, i) => i % 2 !== 0).map((player, i) => (
                        <div
                            key={`${player.persistentId || 'unknown'}-${i}`}
                            className={`player-card ${playerAnswerState(player)} ${!revealed && player.lastAnswer ? 'locked' : ''}`}
                        >
                            <div className="player-name">{player.name}</div>
                            <div className="player-score">{player.score}</div>
                            {revealed && player.lastAnswer && (
                                <div className="player-answer">Antwort: {player.lastAnswer}</div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {timeLeft === 0 && isCreator && !gameOver && currentIndex < questions.length - 1 && (
                <div className="next-question">
                    <button className="button button-primary" onClick={actions.nextQuestion}>
                        <i className="fas fa-arrow-right"></i> Nächste Frage
                    </button>
                </div>
            )}

            {(timeLeft === 0 && currentIndex >= questions.length - 1 || gameOver) && (
                <div className="gameover-actions">
                    <button className="button button-primary" onClick={() => actions.setShowFinalScreen(true)}>
                        <i className="fas fa-arrow-right"></i> Zur Übersicht
                    </button>
                    <button className="button button-primary" onClick={actions.handleReturnToLobby}>
                        <i className="fas fa-arrow-left"></i> Zurück zur Lobby
                    </button>
                </div>
            )}
        </div>
    );
}